import { useEffect, useMemo, useState } from 'react'

import i18n from 'commons/libs/i18n'
import {
  Accordion,
  DatePicker,
  type DefaultCustomerType,
  DSInputType,
  Input,
  type OptionProps,
  Select,
  Typography,
} from 'components'
import {
  channelOptions,
  OVTypes,
  organizationOptions,
  SampleOrderCostElement,
  sampleOrderCostElementOptions,
  sectorOptions,
  yesNoOptions,
} from 'constants/enums'
import dayjs from 'dayjs'
import { Controller, useFormContext } from 'react-hook-form'
import { useMatch } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getReasonsPromotion } from 'services/shared/reasonsPromotion'
import type { ReasonsPromotion } from 'services/shared/reasonsPromotion/types'
import { useSelector } from 'store/store'

import CustomerSelector from './components/CustomerSelector'
import PaymentSelector from './components/PaymentSelector'
import * as S from './styles'

interface InternationalSampleHeaderProps {
  defaultCustomer?: DefaultCustomerType
  defaultPayment?: string
}

// TODO: replace direct dayjs() usage with utils

export const InternationalSampleHeader = ({
  defaultCustomer,
  defaultPayment,
}: InternationalSampleHeaderProps) => {
  const { control, getValues, watch, setValue } = useFormContext()

  const isSalesOrder = !!useMatch('/gerenciar/pedido/:orderNumber')
  const isOvManage = !!useMatch('/gerenciar/ordem-venda/:ovNumber')
  const isSimulateOrder = !!useMatch('/pedidos/:ovNumber/simula')

  const { draftBody } = useSelector((state) => state.salesOrder)
  const { orderStep } = useSelector((store) => store.structure)

  const [clearNumber, setClearNumber] = useState<number>(0)
  const [promotionReasons, setPromotionReasons] = useState<ReasonsPromotion[]>([])
  const [addressOptions, setAddressOptions] = useState<OptionProps[]>([])

  const disableHeader = useMemo(() => {
    if (isSalesOrder) {
      return (
        orderStep !== 0 ||
        draftBody?.cd_stat_pedi_rasc !== 0 ||
        draftBody?.totalizador?.qt_tota_item_soli > 0
      )
    }

    if (isSimulateOrder) {
      return true
    }

    return isOvManage
  }, [draftBody, orderStep, isSalesOrder, isSimulateOrder, isOvManage])

  const fetchReasonsPromotion = async () => {
    try {
      const promotionReasons = await getReasonsPromotion(OVTypes.YASC)
      if (promotionReasons?.length) setPromotionReasons(promotionReasons)
    } catch {
      toast.error(i18n.t('common.errors.unableToFetchOptions'))
    }
  }

  useEffect(() => {
    fetchReasonsPromotion()
  }, [])

  const orderType = watch('cd_tipo_orde')
  const selectedSector = watch('cd_seto_atvd')
  const costElementOption = watch('op_elem_cust')

  return (
    <Accordion
      defaultExpanded={!isOvManage}
      summaryContent={
        <Typography design="hav-title-text">
          {i18n.t('screens.sampleOrder.titleInternational')}
        </Typography>
      }
    >
      <S.Divider />
      <S.GridContainer>
        <S.FieldWrapper className="div-ov-type">
          <S.CustomLabel>{i18n.t('components.orderHeader.orderType')}</S.CustomLabel>
          <S.TextWrapper>
            <Typography design="hav-default-03-text">{orderType}</Typography>
          </S.TextWrapper>
        </S.FieldWrapper>

        <S.FieldWrapper className="div-channel">
          <S.CustomLabel>{i18n.t('components.orderHeader.market')}</S.CustomLabel>
          <S.TextWrapper>
            <Typography design="hav-default-03-text">
              {channelOptions.find((channel) => channel.value === getValues('cd_canl_dstb'))
                ?.title || getValues('cd_canl_dstb')}
            </Typography>
          </S.TextWrapper>
        </S.FieldWrapper>

        <S.FieldWrapper className="div-organization">
          <S.CustomLabel>{i18n.t('components.orderHeader.salesOrganization')}</S.CustomLabel>
          <S.TextWrapper>
            <Typography design="hav-default-03-text">
              {organizationOptions.find((org) => org.value === getValues('cd_orgn_vend'))?.title ||
                getValues('cd_orgn_vend')}
            </Typography>
          </S.TextWrapper>
        </S.FieldWrapper>

        <S.FieldWrapper className="div-sector">
          <Controller
            control={control}
            name="cd_seto_atvd"
            render={({ field: { name, value, onChange } }) => (
              <Select
                defaultValue={sectorOptions[0].value}
                disabled={disableHeader || !!isSalesOrder}
                focused={false}
                id="cd_seto_atvd"
                label={i18n.t('components.orderHeader.sector')}
                menuText={i18n.t('common.placeholders.clickToSelect')}
                name={name}
                onChange={(e) => {
                  onChange(e)
                  setClearNumber(1)
                }}
                options={sectorOptions}
                value={value}
              />
            )}
          />
        </S.FieldWrapper>

        <CustomerSelector
          clearNumber={clearNumber}
          defaultCustomer={defaultCustomer}
          disabled={disableHeader || !selectedSector || !!isSalesOrder}
          setAddressOptions={setAddressOptions}
          setClearNumber={setClearNumber}
        />

        <S.FieldWrapper className="div-office">
          <S.CustomLabel>{i18n.t('components.orderHeader.office')}</S.CustomLabel>
          <S.TextWrapper>
            <Typography design="hav-default-03-text" id="cd_escr_vend">
              {getValues('cd_escr_vend') || '-'}
            </Typography>
          </S.TextWrapper>
        </S.FieldWrapper>

        <S.FieldWrapper className="div-client-address">
          {addressOptions.length > 1 ? (
            <Controller
              control={control}
              defaultValue=""
              name="cd_clie_entr"
              render={({ field: { value, onChange, name } }) => (
                <Select
                  disabled={!addressOptions.length || disableHeader || !selectedSector}
                  id="end-entrega"
                  label={i18n.t('components.orderHeader.deliveryAddress')}
                  menuText={i18n.t('common.placeholders.clickToSelect')}
                  name={name}
                  onChange={(e) => {
                    onChange(e)
                  }}
                  options={addressOptions}
                  value={value}
                />
              )}
            />
          ) : (
            <>
              <S.CustomLabel>{i18n.t('components.orderHeader.deliveryAddress')}</S.CustomLabel>
              <S.TextWrapper>
                <Typography design="hav-default-03-text" id="end-entrega-solo">
                  {addressOptions.length ? addressOptions[0].title : '-'}
                </Typography>
              </S.TextWrapper>
            </>
          )}
        </S.FieldWrapper>

        <S.FieldWrapper className="div-order-month">
          <Controller
            control={control}
            name="cd_mes_pedi"
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                disabled={true}
                disableMaskedInput={false}
                error={fieldState.error?.message}
                height={30}
                inputFormat="MM/YYYY"
                label={i18n.t('components.orderHeader.orderMonth')}
                placeholder={i18n.t('common.placeholders.select')}
                value={dayjs() as unknown as string}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-order-number">
          <Controller
            control={control}
            defaultValue=""
            name="nr_pedi_clie"
            render={({ field: { name, value, onChange } }) => (
              <Input
                design={DSInputType.SEARCH_INPUT}
                disabled={disableHeader}
                InputProps={{
                  fullWidth: true,
                }}
                id="nr_pedi_clie"
                label={i18n.t('components.orderHeader.orderNumber')}
                labelAdornment={i18n.t('components.orderHeader.optional')}
                maxLenghtPosition="top"
                maxLength={20}
                name={name}
                onChange={(e) => {
                  onChange(e)
                }}
                placeholder={i18n.t('common.placeholders.clickToWrite')}
                showMaxLength
                type="text"
                value={value as string}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-shipment-date">
          <Controller
            control={control}
            name="dt_dsjd_pedi"
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                disabled={disableHeader}
                error={fieldState.error?.message}
                height={30}
                label={i18n.t('components.orderHeader.shipmentDate')}
                maxDate={dayjs().add(5, 'months').endOf('month') as unknown as string}
                minDate={dayjs().startOf('day') as unknown as string}
                placeholder={i18n.t('common.placeholders.select')}
                value={field.value}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-invoice-month">
          <Controller
            control={control}
            name="cd_mes_fatm"
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                disabled={disableHeader}
                disableMaskedInput={false}
                error={fieldState.error?.message}
                height={30}
                inputFormat="MM/YYYY"
                label={i18n.t('components.orderHeader.invoiceMonth')}
                maxDate={dayjs().add(5, 'months').endOf('month') as unknown as string}
                minDate={dayjs().startOf('day') as unknown as string}
                placeholder={i18n.t('common.placeholders.select')}
                views={['month']}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-reasons">
          <Controller
            control={control}
            name="cd_moti_prom"
            render={({ field: { name, value, onChange } }) => (
              <Select
                disabled={disableHeader || !promotionReasons.length}
                focused={false}
                id="cd_moti_prom"
                label={i18n.t('components.orderHeader.reason')}
                menuText={i18n.t('common.placeholders.clickToSelect')}
                name={name}
                onChange={(e) => onChange(e)}
                options={promotionReasons.map((reason) => {
                  return {
                    title: `${reason.id} - ${reason.description}`,
                    value: reason.id,
                  }
                })}
                value={value ?? ''}
              />
            )}
          />
        </S.FieldWrapper>

        <PaymentSelector defaultPayment={defaultPayment} disabled={disableHeader} />

        <S.FieldWrapper className="div-delivery-date">
          <Controller
            control={control}
            name="cd_mes_entr"
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                disabled={disableHeader}
                error={fieldState.error?.message}
                height={30}
                label={i18n.t('components.orderHeader.deliveryDate')}
                maxDate={dayjs().add(5, 'months').endOf('month') as unknown as string}
                minDate={dayjs().startOf('day') as unknown as string}
                placeholder={i18n.t('common.placeholders.select')}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-destination-wh">
          <Controller
            control={control}
            defaultValue=""
            name="ds_armz"
            render={({ field: { name, value, onChange } }) => (
              <Input
                design={DSInputType.SEARCH_INPUT}
                disabled={disableHeader}
                InputProps={{
                  fullWidth: true,
                }}
                id="ds_armz"
                label={i18n.t('components.orderHeader.destinationWH')}
                maxLenghtPosition="top"
                maxLength={55}
                name={name}
                onChange={(e) => {
                  onChange(e)
                }}
                placeholder={i18n.t('common.placeholders.clickToWrite')}
                showMaxLength
                type="text"
                value={value as string}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-invoice-text">
          <Controller
            control={control}
            defaultValue=""
            name="ds_text_nota_fisc"
            render={({ field: { name, value, onChange } }) => (
              <Input
                design={DSInputType.SEARCH_INPUT}
                disabled={disableHeader}
                InputProps={{
                  fullWidth: true,
                }}
                id="ds_text_nota_fisc"
                label={i18n.t('components.orderHeader.invoiceText')}
                labelAdornment={'(opcional)'}
                maxLenghtPosition="top"
                maxLength={255}
                name={name}
                onChange={(e) => {
                  onChange(e)
                }}
                placeholder={i18n.t('common.placeholders.clickToWrite')}
                showMaxLength
                type="text"
                value={value as string}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-exchange-cover">
          <Controller
            control={control}
            name="fl_cbtr_camb"
            render={({ field: { name, value, onChange } }) => (
              <Select
                disabled={disableHeader}
                focused={false}
                id="fl_cbtr_camb"
                label={i18n.t('components.orderHeader.exchangeCover')}
                menuText={i18n.t('common.placeholders.clickToSelect')}
                name={name}
                onChange={(e) => onChange(e)}
                options={yesNoOptions}
                value={value ?? ''}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-cost-element">
          <Controller
            control={control}
            name="op_elem_cust"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Select
                disabled={disableHeader}
                error={error?.message}
                label={i18n.t('components.orderHeader.costElement')}
                menuText={i18n.t('common.placeholders.clickToSelect')}
                name={name}
                onChange={(value) => {
                  setValue('cd_cent_cust', '')
                  setValue('cd_elem_pep', '')
                  onChange(value)
                }}
                options={sampleOrderCostElementOptions}
                value={value ?? ''}
              />
            )}
          />
        </S.FieldWrapper>

        <S.FieldWrapper className="div-selected-cost-element">
          {costElementOption === SampleOrderCostElement.COST && (
            <Controller
              control={control}
              name="cd_cent_cust"
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <div>
                  <Input
                    disabled={disableHeader}
                    error={error?.message}
                    id="cd_cent_cust"
                    label={i18n.t('components.orderHeader.costCenter')}
                    labelAdornment={!getValues('cd_cent_cust') && <S.EmptyFieldBadge />}
                    name={name}
                    onChange={onChange}
                    placeholder={i18n.t('common.placeholders.clickToWrite')}
                    type="text"
                    value={value as string}
                  />
                </div>
              )}
            />
          )}
          {costElementOption === SampleOrderCostElement.PEP && (
            <Controller
              control={control}
              name="cd_elem_pep"
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <div>
                  <Input
                    disabled={disableHeader}
                    error={error?.message}
                    id="cd_elem_pep"
                    label={i18n.t('components.orderHeader.pepElement')}
                    labelAdornment={!getValues('cd_elem_pep') && <S.EmptyFieldBadge />}
                    name={name}
                    onChange={onChange}
                    placeholder={i18n.t('common.placeholders.clickToWrite')}
                    type="text"
                    value={value as string}
                  />
                </div>
              )}
            />
          )}
        </S.FieldWrapper>
      </S.GridContainer>
    </Accordion>
  )
}
